/**
 * ESCAPE AND OUTSIDE-CLICK, FOR A POPOVER MENU.
 *
 * Two menus on the dashboard — the export popover and the visible-columns
 * picker — declare `role="menu"` with `role="menuitem"` children and implement
 * none of the behaviour the role promises: Escape does nothing, clicking
 * elsewhere does nothing, and focus is never returned to the button that opened
 * them. A keyboard user who opens one has no way to close it without activating
 * something.
 *
 * ── WHY NOT useDialogFocus ──
 * A menu is not a dialog. It must NOT trap focus (Tab should move out and close
 * it, which is the standard behaviour), it must not lock the page, and it must
 * not sit on the modal stack — a menu open inside a modal has to lose Escape to
 * itself first, which this achieves by listening in the capture phase and
 * stopping there.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ──
 * No arrow-key roving between items. That is the full menu pattern, and these
 * two are short lists of buttons where Tab already works; adding a roving
 * tabindex would be a bigger behavioural change than the defect warrants.
 */
import { useEffect, RefObject } from 'react';

export const useMenuDismiss = (
  open: boolean,
  close: () => void,
  refs: {
    /** The popover itself. */
    menu: RefObject<HTMLElement>;
    /** The control that opened it — a click here is a TOGGLE, not an outside
     *  click, and must not be handled twice. */
    trigger?: RefObject<HTMLElement>;
  }
): void => {
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Capture phase + stopPropagation: a menu opened inside a modal is the
      // innermost layer, so it takes this key and the modal does not also close.
      event.stopPropagation();
      close();
    };

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;

      if (refs.menu.current?.contains(target)) return;
      if (refs.trigger?.current?.contains(target)) return;

      close();
    };

    document.addEventListener('keydown', onKeyDown, true);
    // mousedown, not click: a click whose press began inside the menu and ended
    // outside it (a drag over a label) would otherwise close it mid-gesture.
    document.addEventListener('mousedown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close, refs.menu, refs.trigger]);
};
